import { parseQuery } from "@plannello/core/query/parse";
import type { QuerySort } from "@plannello/shared";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * URL state of every item view. Filter and layout: `?q=` filter text, `?group=`, `?sort=`,
 * `?peek=KEY`. Display options (components/views/viewState.ts): `?sub=` sub-grouping, `?cols=`
 * visible properties (comma list), `?subs=off` hides sub-items, `?density=`, timeline `?rows=` and
 * `?zoom=`. `?view=<id>` names the saved view the state was loaded from (dirty tracking).
 */
export type ViewSearch = {
  q?: string;
  group?: string;
  sort?: string;
  peek?: string;
  view?: string;
  sub?: string;
  cols?: string;
  subs?: string;
  density?: string;
  rows?: string;
  zoom?: string;
};

/** Search values arrive JSON-parsed by the router ("2" becomes 2); keep every value a string. */
const str = (v: unknown) =>
  typeof v === "string" ? v || undefined : typeof v === "number" || typeof v === "boolean" ? String(v) : undefined;

export function validateViewSearch(s: Record<string, unknown>): ViewSearch {
  const out: ViewSearch = { q: str(s.q), group: str(s.group), sort: str(s.sort), peek: str(s.peek) };
  for (const k of ["view", "sub", "cols", "subs", "density", "rows", "zoom"] as const) {
    const v = str(s[k]);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** `?sort=priority` / `?sort=updated-asc` into a query sort (same syntax as `sort:` clauses). */
export function parseSortParam(raw: string | undefined): QuerySort[] | undefined {
  if (!raw) return undefined;
  const sorts = parseQuery(`sort:${raw}`).sort;
  return sorts.length ? sorts : undefined;
}

export const SORT_KEYS = ["rank", "priority", "updated", "created", "due", "title", "number"] as const;

/** Updates the view's search params in place (replace, so typing does not flood history). */
export function useSetViewSearch() {
  const navigate = useNavigate();
  return useCallback(
    (patch: Partial<ViewSearch>, opts: { push?: boolean } = {}) =>
      void navigate({
        to: ".",
        search: ((prev: ViewSearch) => {
          const next: ViewSearch = { ...prev, ...patch };
          for (const k of Object.keys(next) as (keyof ViewSearch)[]) if (!next[k]) delete next[k];
          return next;
        }) as never,
        replace: !opts.push,
      }),
    [navigate],
  );
}
